import { type FC, type PropsWithChildren } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  Notification,
  notificationDismiss,
  toTimeAgo,
  TriggerButton,
  VStack,
} from "widgets";

import { type Notification as NotificationGql, NotificationTypeEnum, type User } from "@/graphql.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.ts";
import { markAsRead } from "@/redux/modules/notifications.tsx";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { Avatar } from "@/ui-modules/core/UserAvatar.tsx";
import { GroupNotification } from "@/ui-modules/notifications/GroupNotification.tsx";
import { JobNotification } from "@/ui-modules/notifications/JobNotification.tsx";
import WorkflowNotification from "@/ui-modules/notifications/WorkflowNotification.tsx";
import { ProjectActivityMessage } from "@/ui-modules/projects/ProjectActivityMessage.tsx";

import { getNotificationIntent, useGetNotificationUser } from "./utils/utils.ts";

const NotificationContent: FC<{ notification: NotificationGql }> = ({ notification }) => {
  switch (notification.type) {
    case NotificationTypeEnum.Job: {
      return <JobNotification notification={notification} />;
    }
    case NotificationTypeEnum.ProjectActivity: {
      const userList: User[] = notification.activity
        ? [notification.activity.user].concat(notification.activity.affectedUsers ?? [])
        : [];
      return notification.activity ? (
        <ProjectActivityMessage
          activity={notification.activity}
          users={userList}
          forNotification={true}
          notificationId={notification.id}
        />
      ) : null;
    }
    case NotificationTypeEnum.Workflow: {
      return <WorkflowNotification notification={notification} />;
    }
    case NotificationTypeEnum.Group: {
      return <GroupNotification notification={notification} />;
    }
    // No default
  }
  return null;
};

export interface FromNotificationProps {
  notification: NonNullable<NotificationGql>;
  toastId?: number | string;
  markAsReadOverInteraction?: boolean; // in some cases e.g. alerts we want to mark the notif as read over interactions
}

export const FromNotification: FC<FromNotificationProps> = ({ notification, toastId, markAsReadOverInteraction }) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const intent = getNotificationIntent(notification);
  const user = useGetNotificationUser(notification);
  const yourself = useSelector<IAppState, boolean>((state) => state.auth.session.id === user?.id);

  // do not mark notification as read when it disappears as it will remove
  // the badge on the notifications' dropdown, see SEL9-532

  if (
    notification.type !== NotificationTypeEnum.Job &&
    notification.type !== NotificationTypeEnum.ProjectActivity &&
    notification.type !== NotificationTypeEnum.Workflow &&
    notification.type !== NotificationTypeEnum.Group
  ) {
    return null;
  }

  return (
    <div onClickCapture={markAsReadOverInteraction ? () => dispatch(markAsRead(notification.id)) : undefined}>
      <Notification
        title={yourself ? "You" : getUserDisplayName(user)}
        subtitle={notification.created ? toTimeAgo(notification.created) : undefined}
        currentToastId={toastId}
        intent={intent}
        actions={[
          !notification.read && (
            <TriggerButton
              icon="mark_chat_read"
              label="Mark as Read"
              badge={true}
              onPress={() => {
                void dispatch(markAsRead(notification.id));
                if (toastId) {
                  notificationDismiss(toastId);
                }
              }}
            />
          ),
          yourself ? null : user ? <Avatar user={user} size={18} key="avatar" /> : null,
          toastId && (
            <TriggerButton
              icon="close"
              label="Dismiss"
              onPress={() => {
                notificationDismiss(toastId);
                void dispatch(markAsRead(notification.id));
              }}
              key="close"
              style={{ color: "#203647" }}
            />
          ),
        ]}
      >
        <NotificationContent notification={notification} />
      </Notification>
    </div>
  );
};

export interface ErrorNotificationProps {
  seeMoreDetailsLink?: boolean;
}

export const ErrorNotificationChildren: FC<PropsWithChildren<ErrorNotificationProps>> = (props) => {
  const navigate = useNavigate();

  return (
    <VStack>
      <div>{props.children}</div>
      {props.seeMoreDetailsLink && (
        <div>
          <ActionButton
            label="See More Details"
            onPress={() => navigate("/errors")}
            intent={ActionButtonIntent.Danger}
            size={ActionButtonSize.Small}
          />
        </div>
      )}
    </VStack>
  );
};
